import type {
  ProductPhotoCutout,
  ProductPhotoCutoutJob,
  ProductPhotoSimple,
  ProductPhotoSource,
  ProductPhotos,
} from "@nova-ai/shared"
import { ImageOff, LoaderCircle, Scissors } from "lucide-react"

import { useProductPhotos } from "../../services/use-product-photos"
import { QueryState } from "../layout/query-state"
import { Button } from "../ui/button"
import { Callout } from "../ui/callout"
import { StatusBadge, type BadgeTone } from "../ui/status-badge"

export function ProductPhotosSection({ configSku, store }: { configSku: string; store: string }) {
  const photos = useProductPhotos(configSku, store)
  const actionError = photos.assign.error ?? photos.generate.error

  return (
    <section aria-labelledby="product-photos-heading" className="space-y-3">
      <header>
        <h2
          id="product-photos-heading"
          className="text-[15px] font-semibold tracking-tight text-foreground"
        >
          Product photos
        </h2>

        <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
          Magento main photos are ready automatically. Assign a shared gallery photo to one exact
          simple before generating its transparent cutout.
        </p>
      </header>

      {actionError ? (
        <Callout tone="destructive" title="Photo action failed" dense>
          {actionError.message}
        </Callout>
      ) : null}

      {isProductPhotoReadTruncated(photos.data) ? (
        <Callout tone="warning" title="Product photo history is truncated" dense>
          This view shows a bounded sample. Narrow to the exact simple or inspect retained Gallery
          approvals before cleanup.
        </Callout>
      ) : null}

      <QueryState
        isLoading={photos.isLoading}
        error={photos.error}
        isEmpty={!hasProductPhotoSources(photos.data)}
        emptyMessage={`This product has no Magento photos mirrored in ${store}.`}
      >
        <PhotoList
          data={photos.data}
          assigning={photos.assign.isPending ? photos.assign.variables : undefined}
          generating={photos.generate.isPending ? photos.generate.variables : undefined}
          onAssign={(input) => photos.assign.mutate(input)}
          onGenerate={(input) => photos.generate.mutate(input)}
        />
      </QueryState>
    </section>
  )
}

function isProductPhotoReadTruncated(data: ProductPhotos | undefined): boolean {
  return [
    data?.simplesTruncated,
    data?.sourcesTruncated,
    data?.assignmentsTruncated,
    data?.cutoutsTruncated,
    data?.jobsTruncated,
    data?.usageTruncated,
    data?.approvalContextTruncated,
  ].some((isTruncated) => isTruncated === true)
}

function hasProductPhotoSources(data: ProductPhotos | undefined): boolean {
  return Boolean(data?.simples.some((simple) => simple.sources.length > 0))
}

function PhotoList({
  data,
  assigning,
  generating,
  onAssign,
  onGenerate,
}: {
  data: ProductPhotos | undefined
  assigning: { simpleSku: string; sourceKey: string } | undefined
  generating: { simpleSku: string; sourceKey: string } | undefined
  onAssign: (input: { simpleSku: string; sourceKey: string }) => void
  onGenerate: (input: { simpleSku: string; sourceKey: string }) => void
}) {
  return (
    <div className="divide-y divide-border rounded-xl border border-border bg-card shadow-card">
      {(data?.simples ?? []).map((simple) => (
        <SimplePhotos
          key={simple.simpleSku}
          simple={simple}
          assigning={assigning}
          generating={generating}
          onAssign={onAssign}
          onGenerate={onGenerate}
        />
      ))}
    </div>
  )
}

function SimplePhotos({
  simple,
  assigning,
  generating,
  onAssign,
  onGenerate,
}: {
  simple: ProductPhotoSimple
  assigning: { simpleSku: string; sourceKey: string } | undefined
  generating: { simpleSku: string; sourceKey: string } | undefined
  onAssign: (input: { simpleSku: string; sourceKey: string }) => void
  onGenerate: (input: { simpleSku: string; sourceKey: string }) => void
}) {
  return (
    <section className="p-4">
      <div className="mb-3 flex flex-wrap items-baseline gap-2">
        <h3 className="text-sm font-medium text-foreground">{variantLabel(simple)}</h3>

        <span className="font-mono text-xs text-muted-foreground">{simple.simpleSku}</span>
      </div>

      <ul className="space-y-3">
        {simple.sources.map((source) => {
          const identity = { simpleSku: simple.simpleSku, sourceKey: source.sourceKey }
          return (
            <li key={source.sourceKey} className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <PhotoPreview source={source} />

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{sourceLabel(source)}</span>

                  {!source.isCurrent ? (
                    <StatusBadge tone="warning" size="sm">
                      Source changed
                    </StatusBadge>
                  ) : null}

                  {source.role === "shared_secondary" && source.isAssigned ? (
                    <StatusBadge tone="info" size="sm">
                      Assigned
                    </StatusBadge>
                  ) : null}
                </div>

                <p className="mt-1 truncate text-xs text-muted-foreground" title={source.url}>
                  {source.url}
                </p>

                <CutoutSummary
                  cutouts={source.cutouts}
                  job={source.latestJob}
                  isGenerating={
                    samePhoto(generating, identity) ||
                    source.latestJob?.status === "queued" ||
                    source.latestJob?.status === "running"
                  }
                />
              </div>

              <div className="shrink-0">
                <PhotoAction
                  source={source}
                  identity={identity}
                  assigning={assigning}
                  generating={generating}
                  onAssign={onAssign}
                  onGenerate={onGenerate}
                />
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function PhotoAction({
  source,
  identity,
  assigning,
  generating,
  onAssign,
  onGenerate,
}: {
  source: ProductPhotoSource
  identity: { simpleSku: string; sourceKey: string }
  assigning: { simpleSku: string; sourceKey: string } | undefined
  generating: { simpleSku: string; sourceKey: string } | undefined
  onAssign: (input: { simpleSku: string; sourceKey: string }) => void
  onGenerate: (input: { simpleSku: string; sourceKey: string }) => void
}) {
  if (!source.isCurrent) {
    return null
  }
  if (!source.isAssigned) {
    const pending = samePhoto(assigning, identity)
    return (
      <Button size="sm" disabled={pending} onClick={() => onAssign(identity)}>
        {pending ? <LoaderCircle className="size-3.5 animate-spin" /> : null}

        <span>Assign to {identity.simpleSku}</span>
      </Button>
    )
  }
  const pending =
    samePhoto(generating, identity) ||
    source.latestJob?.status === "queued" ||
    source.latestJob?.status === "running"
  return (
    <Button size="sm" disabled={pending} onClick={() => onGenerate(identity)}>
      {pending ? (
        <LoaderCircle className="size-3.5 animate-spin" />
      ) : (
        <Scissors className="size-3.5" />
      )}

      {pending ? "Generating" : "Generate cutout"}
    </Button>
  )
}

function PhotoPreview({ source }: { source: ProductPhotoSource }) {
  const available = source.cutouts.find((cutout) => cutout.state === "available" && cutout.url)
  return (
    <div className="grid shrink-0 grid-cols-2 overflow-hidden rounded-lg border border-border">
      <img
        className="size-20 object-cover"
        src={source.url}
        alt={`${sourceLabel(source)} source`}
        loading="lazy"
        decoding="async"
      />

      <div className="flex size-20 items-center justify-center bg-muted/60">
        {available?.url ? (
          <img
            className="size-full object-contain p-1"
            src={available.url}
            alt={`${sourceLabel(source)} transparent cutout`}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <ImageOff className="size-5 text-muted-foreground/60" aria-hidden="true" />
        )}
      </div>
    </div>
  )
}

function CutoutSummary({
  cutouts,
  job,
  isGenerating,
}: {
  cutouts: ProductPhotoCutout[]
  job: ProductPhotoCutoutJob | null
  isGenerating: boolean
}) {
  if (cutouts.length === 0 && !isGenerating && job?.status !== "failed") {
    return <p className="mt-2 text-xs text-muted-foreground">Cutout not generated</p>
  }
  return (
    <div className="mt-2 flex flex-wrap items-start gap-2">
      {isGenerating ? (
        <StatusBadge tone="info" size="sm">
          <LoaderCircle className="size-3 animate-spin" /> Generating
        </StatusBadge>
      ) : null}

      {job?.status === "failed" ? (
        <StatusBadge tone="destructive" size="sm">
          Failed: {job.error?.message ?? "cutout job failed"}
        </StatusBadge>
      ) : null}

      {cutouts.map((cutout) => (
        <details key={cutout.id} className="text-xs text-muted-foreground">
          <summary className="cursor-pointer list-none rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <StatusBadge tone={cutoutTone(cutout)} size="sm">
              {cutoutLabel(cutout)}

              {cutout.retainedUsageCount > 0 ? ` · used ${cutout.retainedUsageCount}×` : ""}
            </StatusBadge>
          </summary>

          <dl className="mt-2 grid max-w-xl grid-cols-[auto_1fr] gap-x-2 gap-y-1 pl-2">
            <dt>Source digest</dt>

            <dd className="break-all font-mono">{cutout.sourceDigest}</dd>

            <dt>Cutout hash</dt>

            <dd className="break-all font-mono">{cutout.artifactHash ?? "Not published"}</dd>
          </dl>

          {cutout.retainedUsage.length > 0 ? (
            <ul className="mt-2 space-y-1 pl-2">
              {cutout.retainedUsage.map((usage) => (
                <li key={`${usage.consumerType}:${usage.label}:${usage.approvalIds.join(",")}`}>
                  <span>{usage.label}</span>

                  {usage.approvalIds.length > 0
                    ? ` · approvals ${usage.approvalIds.join(", ")}`
                    : ""}

                  {usage.isStale ? " · stale draft" : ""}
                </li>
              ))}

              {cutout.retainedUsageTruncated ? <li>More retained usage is not shown.</li> : null}
            </ul>
          ) : null}
        </details>
      ))}
    </div>
  )
}

function cutoutTone(cutout: ProductPhotoCutout): BadgeTone {
  if (cutout.state === "available") {
    return "success"
  }
  if (cutout.state === "stale") {
    return "warning"
  }
  return "destructive"
}

function cutoutLabel(cutout: ProductPhotoCutout): string {
  if (cutout.state === "available") {
    return "Cutout available"
  }
  if (cutout.state === "stale") {
    return "Cutout stale"
  }
  return cutout.failureReason ? `Failed: ${cutout.failureReason}` : "Cutout failed"
}

function sourceLabel(source: ProductPhotoSource): string {
  return source.role === "main" ? "Main photo" : `Shared secondary · position ${source.position}`
}

function variantLabel(simple: ProductPhotoSimple): string {
  const axes = [simple.flavor, simple.size].filter((axis): axis is string => Boolean(axis?.trim()))
  return axes.length > 0 ? axes.join(" · ") : "Variant"
}

function samePhoto(
  pending: { simpleSku: string; sourceKey: string } | undefined,
  current: { simpleSku: string; sourceKey: string }
): boolean {
  return pending?.simpleSku === current.simpleSku && pending.sourceKey === current.sourceKey
}
